import { queryOptions } from '@tanstack/react-query';
import type { LeaderSwapDto, PositionDto, SwapSide, SwapVenue, TradeDto } from '@ultrasnip/contracts';
import { api } from '@/lib/api-client';

// Mirrors apps/api/src/modules/tokens/token-overview.types.ts (GET /api/tokens/:mint/overview).

export type DangerousExtension =
  | 'DefaultAccountState'
  | 'NonTransferable'
  | 'PermanentDelegate'
  | 'TransferHook'
  | 'Pausable';

/**
 * Name, symbol and image come from metadata; the rest from the mint account, `null` while the chain
 * can't be read (`chainUnavailable`), when a `null` authority means unknown, not revoked.
 */
export interface TokenOverviewTokenDto {
  mint: string;
  symbol: string | null;
  name: string | null;
  image: string | null;
  /** The mint's decimals; while the chain can't be read, the metadata's (null when unknown). */
  decimals: number | null;
  tokenProgram: 'spl-token' | 'token-2022' | null;
  /** Raw units (u64 as a decimal string). */
  supply: string | null;
  mintAuthority: string | null;
  freezeAuthority: string | null;
  dangerousExtensions: DangerousExtension[] | null;
}

/** All `null` while the chain can't be read. */
export interface TokenOverviewMarketDto {
  venue: SwapVenue | null;
  priceUsd: number | null;
  priceSol: number | null;
  marketCapSol: number | null;
  marketCapUsd: number | null;
  curveProgressPct: number | null;
  complete: boolean | null;
  mayhem: boolean | null;
}

/** One fill: SOL per whole token, by a tracked leader or by the user. */
export interface PricePointDto {
  t: string;
  priceSol: number;
  side: SwapSide;
  source: 'leader' | 'you';
  /** SOL paid (buy) or received (sell) in the fill. */
  sol: number;
  /** The tracked wallet behind a leader fill (null for the user's own). */
  trackedWalletId: string | null;
}

/** The user's result in this coin, current execution mode, open and closed positions. */
export interface TokenOverviewResultDto {
  positions: number;
  openPositions: number;
  costLamports: string;
  realizedLamports: string;
  unrealizedLamports: string;
  /** Null when no position has a known value yet. */
  pnlLamports: string | null;
  pnlPct: number | null;
}

export interface TokenOverviewDto {
  token: TokenOverviewTokenDto;
  market: TokenOverviewMarketDto;
  /**
   * The server couldn't read the mint and its curve from Solana (rate limits): the token's on-chain
   * facts and the market are unknown; everything else is complete.
   */
  chainUnavailable: boolean;
  /** Last 7 days (`SERIES_WINDOW_DAYS`), oldest first (empty when the decimals are unknown). */
  priceSeries: PricePointDto[];
  /** Newest first, at most `LEADER_TRADES_LIMIT`. */
  leaderTrades: LeaderSwapDto[];
  /** Current execution mode; open first, then newest opened. */
  positions: PositionDto[];
  /** Current execution mode, newest first, at most `TRADES_LIMIT`. */
  trades: TradeDto[];
  /** Null when the user has no position in the coin. */
  result: TokenOverviewResultDto | null;
}

/** The server's caps (token-overview.service.ts). */
export const SERIES_WINDOW_DAYS = 7;
export const LEADER_TRADES_LIMIT = 50;
export const TRADES_LIMIT = 50;

export const TokenKeys = {
  overview: (mint: string) => ['token-overview', mint] as const,
};

export const TOKEN_POLL_MS = 10_000;

export function tokenOverviewQuery(mint: string) {
  return queryOptions({
    queryKey: TokenKeys.overview(mint),
    queryFn: ({ signal }) => api.get<TokenOverviewDto>(`/tokens/${mint}/overview`, { signal }),
    staleTime: 5_000,
    refetchInterval: TOKEN_POLL_MS,
  });
}
